import type { AnswerDetailListItemResponse } from '@coorum-research/shared-common/api-response'
import type { MutableRefObject } from 'react'
import { MyPageContext } from '@coorum-research/core/src/components/MypageContext'
import { MypageHeader } from '@coorum-research/core/src/components/MypageHeader'
import { Sheet } from '@coorum-research/core/src/components/Sheet'
import { defaultTheme, ThemeContext } from '@coorum-research/core/src/components/ThemeContext'
import { MOBILE_DEVICE_REGEX } from '@coorum-research/shared-common/utils/device.util'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { mypageSettingApi } from '../apis/mypage-settings.api'
import { addQueryParams } from '../utils/url.util'
import { AnswerDetail } from './AnswerDetail'
import { MyPageAnswerListContainer } from './MyPageAnswerListContainer'
import { MyPageStampRallyContainer } from './MyPageStampRallyContainer'
import { ThumbnailSelector } from './ThumbnailSelector'

const { getMyPageSetting } = mypageSettingApi()

const isMobile = MOBILE_DEVICE_REGEX.test(navigator.userAgent)

type Props = {
  containerRef: MutableRefObject<HTMLDivElement | null>
}

export function MyPage({ containerRef }: Props) {
  const [tabValue, setTabValue] = useState<string | null>(null)
  const contextValue = useMemo(() => ({ tabValue, setTabValue }), [tabValue, setTabValue])
  const [isModalOpen, setModalOpen] = useState(false)
  const [isThumbnailSelectOpen, setThumbnailSelectOpen] = useState(false)
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null)
  const [answerImages, setAnswerImages] = useState<string[]>([])
  const [selectedTabUrl, setSelectedTabUrl] = useState<string | undefined>(undefined)
  const [selectedAnswer, setSelectedAnswer] = useState<AnswerDetailListItemResponse | null>(null)

  const queryClient = useQueryClient()

  const [headerTabMode, setHeaderTabMode] = useState<string>('home')

  const { data: mypageSetting, isFetched: isMypageSettingFetched } = useQuery({
    queryKey: ['get-mypage-setting'],
    queryFn: () => getMyPageSetting(),
    refetchOnWindowFocus: false,
  })

  const headerTabs = mypageSetting?.headerTabs ?? []

  // モーダルの開閉状態が変更されたときのハンドラ
  const handleModalChange = (isOpen: boolean) => {
    setModalOpen(isOpen)
    if (!isOpen) {
      // モーダルが閉じられたときにデータを再取得
      void queryClient.invalidateQueries({ queryKey: ['user-tagged-answers'] })
      void queryClient.invalidateQueries({ queryKey: ['get-game-stamp-rally'] })
    }
  }

  const handleAnswerClick = (answerId: string) => {
    setSelectedAnswerId(answerId)
    setModalOpen(true)
  }

  const handleThumbnailSubmit = (selectedImage: string | null) => {
    if (selectedImage === null) {
      console.error('No image selected')
      return
    }

    if (selectedTabUrl && selectedAnswer) {
      const url = addQueryParams(selectedTabUrl, [
        { key: 'research_answer_id', value: selectedAnswer.id },
        { key: 'thumbnailUrl', value: selectedImage },
      ])
      if (url && selectedAnswer.answerStatus === 'done') {
        window.open(url, '_blank', 'noopener')
      }
    }

    setThumbnailSelectOpen(false) // モーダルを閉じる
  }

  const {
    theme,
    headingContent,
  } = mypageSetting?.configJson ?? {}

  const themeContextValue = useMemo(() => ({
    ...defaultTheme,
    ...theme,
  }), [theme])

  return (
    <ThemeContext value={themeContextValue}>
      <MyPageContext value={contextValue}>
        <div className="flex h-full flex-col">
          <MypageHeader
            className="mx-4 flex-[0_0_auto]"
            title={headingContent}
            tabs={headerTabs}
            mode={headerTabMode}
            onTabClick={setHeaderTabMode}
          />
          <div className="flex-1 overflow-y-auto pt-4">
            {(headerTabMode === 'home' || headerTabMode === 'calendar')
              ? (
                <MyPageAnswerListContainer
                  containerRef={containerRef}
                  headerTabMode={headerTabMode}
                  mypageSetting={mypageSetting}
                  isMypageSettingFetched={isMypageSettingFetched}
                  setThumbnailSelectOpen={setThumbnailSelectOpen}
                  setAnswerImages={setAnswerImages}
                  setSelectedTabUrl={setSelectedTabUrl}
                  setSelectedAnswer={setSelectedAnswer}
                  handleAnswerClick={handleAnswerClick}
                />
              )
              : (
                <MyPageStampRallyContainer
                  containerRef={containerRef}
                  handleAnswerClick={handleAnswerClick}
                />
              )}
          </div>
        </div>

        {/* 投稿詳細モーダル */}
        <Sheet.Root open={isModalOpen} onOpenChange={handleModalChange}>
          <Sheet.Portal container={containerRef?.current}>
            <Sheet.Overlay />
            <Sheet.Content className="h-full bg-white">
              <Sheet.Title hidden>回答詳細</Sheet.Title>
              <Sheet.Description hidden>回答詳細</Sheet.Description>
              {selectedAnswerId && (
                <AnswerDetail
                  containerRef={containerRef}
                  answerId={selectedAnswerId}
                />
              )}
            </Sheet.Content>
          </Sheet.Portal>
        </Sheet.Root>

        {/* 画像選択モーダル */}
        <Sheet.Root open={isThumbnailSelectOpen} onOpenChange={setThumbnailSelectOpen}>
          <Sheet.Portal container={containerRef?.current}>
            <Sheet.Overlay />
            <Sheet.Content className={clsx('absolute inset-0 m-4 rounded bg-white', { 'h-[calc(100%-4rem)]': isMobile })}>
              <Sheet.Title hidden>投稿サムネイルの選択</Sheet.Title>
              <Sheet.Description hidden>投稿サムネイルの選択</Sheet.Description>
              <ThumbnailSelector
                images={answerImages}
                onSubmit={handleThumbnailSubmit}
              />
            </Sheet.Content>
          </Sheet.Portal>
        </Sheet.Root>
      </MyPageContext>
    </ThemeContext>
  )
}
